import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from "motion/react";
import Sidebar from './components/Sidebar';
import Page1 from './pages/Page1';
import Search from './components/Search';
import Carousel from './components/Carousel';
import CardCarousel from './components/CardCarousel';
import { useAppStore } from './stores/useAppStore';
import Header from './components/Header';
import Logo from './components/Logo';
import IconSearch from './components/IconSearch';
import IconClose from './components/IconClose';

import Home from './pages/Home';
import PageCiscoSpeakingSessions from './pages/PageCiscoSpeakingSessions';
import { CAROUSEL_TYPES } from './constants/carouselTypes';
import CardSpeaker from './components/CardSpeaker';
import PageSweepstakes from './pages/PageSweepstakes';
import PageCiscoAiCareers from './pages/PageCiscoAiCareers';
import { PageAIEra } from './pages/PageAIEra';
import CardBigDemo from './components/CardBigDemo';
import PageCiscoAtAi4 from './pages/PageCiscoAtAi4';

const MotionIconSearch = motion.create(IconSearch);
const MotionIconClose = motion.create(IconClose);
const MotionLogo = motion.create(Logo);
const MotionButton = motion.create('button');

function App() {
  const location = useLocation();
  const { isOpen: carouselIsOpen, currentIndex: carouselIndex, type: carouselType, closeCarousel } = useAppStore((state) => state.carousel);
  const { content, resetMapOptions, menuItems, isLoading, loadContent } = useAppStore();
  const { fullScreen, toggleFullScreen, hideHeader, setHideHeader, menuActiveKey, setMenuActiveKey, setCardTouchPos } = useAppStore();
  const [ prevLocation, setPrevLocation ] = useState(null);
  const { searchIsOpen, setSearchIsOpen } = useAppStore();
  const [ headerTitle, setHeaderTitle ] = useState("");

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const handleContextMenu = (event) => {
        event.preventDefault();
      };

      // document.addEventListener('contextmenu', handleContextMenu);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, []);

  const componentsMap = {
    homepage: Home,
    demos: Page1,
    critical_infrastructure_for_the_ai_era: PageAIEra,
    cisco_speaking_sessions: PageCiscoSpeakingSessions,
    cisco_at_ai4: PageCiscoAtAi4,
    cisco_ai_careers: PageCiscoAiCareers,
    sweepstakes: PageSweepstakes
  };

  useEffect(() => {
    setSearchIsOpen(false);
  }, []);

  useEffect(() => {
    const item = menuItems?.find(item => item.key === menuActiveKey);
    const title = item?.not_in_header ? "" : (item?.title || "");
    setHeaderTitle(title);
  }, [menuActiveKey]);

  useEffect(() => {
    if (prevLocation && prevLocation.pathname === '/demos' && location.pathname !== '/demos') {
      resetMapOptions();
    }
    setPrevLocation(location);

    setHideHeader(location.pathname === '/homepage');
  }, [location]);

  // Cargar el contenido JSON al montar el componente
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (!isLoading) setMenuActiveKey(menuItems[0].key);
  }, [isLoading]);

  const handleButtonClick = (event) => {
    let x, y;

    if (event.touches) {
      // Evento táctil
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // Evento de ratón
      x = event.clientX;
      y = event.clientY;
    }
    setCardTouchPos({x, y});

    // Solo mostrar header si no estamos en homepage o si estamos cerrando fullscreen/carousel
    if (hideHeader && (location.pathname !== '/homepage' || fullScreen || carouselIsOpen)) {
      setHideHeader(false);
    }

    if (fullScreen) toggleFullScreen();
    else if (carouselIsOpen) closeCarousel();
    else setSearchIsOpen(!searchIsOpen)
  };

  if (isLoading) {
    return <div></div>;
  }

  return (
    <div className="select-none relative min-h-screen flex flex-col w-full bg-cover bg-center bg-no-repeat bg-fixed">
      {/* Overlay opcional para mejorar la legibilidad */}
      <div className="absolute inset-0 bg-black bg-opacity-20 pointer-events-none"></div>
      <div className="w-full flex flex-1 relative z-10">
        <Sidebar items={menuItems} />

        <div className="w-full flex-1 min-w-0">
          <Header title={headerTitle} className='w-full' />

          <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
              <Route
                path="/"
                element={<Navigate to={`/${menuItems[0]?.key}`} replace />}
              />
              {menuItems.map((item) => {
                const Component = componentsMap[item.key];
                if (!Component) {
                  console.error(`No se encontró un componente para la clave: ${item.key}`);
                  return null;
                }
                return (
                  <Route
                    key={item.key}
                    path={item.key}
                    element={<Component />}
                  />
                );
              })}
            </Routes>
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.SPEAKERS && (
          <Carousel
            items={content?.cisco_speaking_sessions}
            initialSlide={carouselIndex}
            SlideComponent={CardSpeaker}
            itemWidth="160.5625rem"
            itemHeight="71.125rem"
            prevButtonText="Previous Session"
            nextButtonText="Next Session"
            loop={false}
          />
        )}
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.STORIES && (
          <Carousel
            items={content?.customer_voices}
            initialSlide={carouselIndex}
            SlideComponent={CardCarousel}
          />
        )}
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.DEMO && (
          <Carousel
            items={content?.demos}
            initialSlide={carouselIndex}
            SlideComponent={CardBigDemo}
            prevButtonText="Previous Demo"
            nextButtonText="Next Demo"
          />
        )}
        {searchIsOpen && <Search key="search_layer" items={content.demos} />}
      </AnimatePresence>

      <MotionLogo 
        className="absolute w-[17.625rem] h-auto left-[8.25rem] top-[5.625rem] z-50" 
        animate={{ 
          opacity: hideHeader && fullScreen ? 0 : 1,
          transition: { duration: 0.3, ease: "easeOut" }
        }}
        style={{ pointerEvents: hideHeader && fullScreen ? 'none' : 'auto' }}
      />
      
      <MotionButton
        onClick={(e) => handleButtonClick(e)}
        className="click-effect absolute top-[6.625rem] right-[6.625rem] w-[9.75rem] h-[9.75rem] z-50 justify-center rounded-full focus:outline-none text-white text-[2rem] font-light leading-none flex items-center gap-[3.25rem] overflow-hidden"
        animate={{
          backgroundColor: !searchIsOpen && !fullScreen && !carouselIsOpen ? "#1a2951" : "#ffffff"
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        { !searchIsOpen && !fullScreen && !carouselIsOpen
          ? <MotionIconSearch
              key="icon_search"
              initial={{ x: "-200%" }}
              animate={{ x: 0 }}
              exit={{ y: "-200%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-[3.5rem] h-auto" />
          : <MotionIconClose
              key="icon_close"
              initial={{ x: "200%" }}
              animate={{ x: 0 }}
              exit={{ x: "200%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-[3.5rem] h-auto"
              color="#1C2C58"
            />
        }
      </MotionButton>
      
    </div>
  );
}

export default App;
